import { useState, useRef } from "react";

import "./styles.scss";

import api from "@helpers/api";

import CustomModal from "@components/CustomModal";

import {
    CommunityAssetImageField,
    AssetCategory,
    isValidHttpUrl,
} from "@features/uploadAsset";

import { Input } from "@fragments";

function extractPlaylistEntity(res) {
    const status = res?.status ?? res?.response?.status;
    if (status != null && status >= 400) return null;
    const body = res?.data;
    if (!body) return null;
    return body.data ?? body;
}

export default function ConfigPlaylistModal({ modalRef }) {
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [uploadBusyCount, setUploadBusyCount] = useState(0);
    const [, setTick] = useState(0);

    const REF_config = useRef({});
    const REF_callback = useRef();

    const bump = () => setTick((n) => n + 1);

    const handleUploadingChange = (uploading) => {
        setUploadBusyCount((c) => Math.max(0, c + (uploading ? 1 : -1)));
    };

    modalRef.current = {
        open(preFill, callback) {
            const rawBanner = preFill.banner;
            const trimmed =
                rawBanner != null ? String(rawBanner).trim() : "";
            const bannerUrl = trimmed && isValidHttpUrl(trimmed) ? trimmed : null;

            REF_config.current = {
                ...preFill,
                name: preFill.name ?? "",
                description: preFill.description ?? "",
                banner: bannerUrl,
                section: preFill.section,
                order: preFill.order,
                id: preFill.id,
                community: preFill.community,
            };
            REF_callback.current = callback;
            setOpen(true);
        },
    };

    async function handleSubmit() {
        try {
            setLoading(true);
            const payload = getPayload();
            const sectionId = parseInt(REF_config.current.section, 10);

            let res;
            if (REF_config.current.id) {
                res = await api.patch(
                    `section/playlist/${REF_config.current.id}`,
                    payload
                );
            } else {
                res = await api.post(`section/${sectionId}/playlist`, payload);
            }

            const data = extractPlaylistEntity(res);

            if (data?.id) {
                await REF_callback.current(data);
                setOpen(false);
            }
        } catch (e) {
            console.log(e);
        }

        setLoading(false);

        function getPayload() {
            const { name, description, banner, order } = REF_config.current;

            const data = {
                name: name.trim(),
                description: description?.trim() ?? null,
                order: parseInt(order, 10) || 0,
            };

            if (banner && isValidHttpUrl(banner)) {
                data.banner = banner;
            }

            return data;
        }
    }

    async function handleDelete() {
        try {
            await api.delete(`section/playlist/${REF_config.current.id}`);
            REF_callback.current();
        } catch (e) {
            console.log(e);
        }

        setOpen(false);
    }

    const communityId = REF_config.current.community
        ? parseInt(REF_config.current.community, 10)
        : null;

    return (
        <CustomModal
            maxWidth={450}
            bottomButtonSumbitText="Salvar sessão"
            bottomButtonCancelText="Cancelar"
            loading={loading}
            disabled={uploadBusyCount > 0}
            handleDelete={REF_config.current?.id && handleDelete}
            deletionTitle="Você realmente deseja apagar esta playlist permanentemente?"
            title="Editar playlist"
            handleSubmit={handleSubmit}
            open={open}
            handleClose={() => setOpen(false)}
            bodyComponent={
                open && communityId ? (
                    <div className="px-4 pb-1 py-3">
                        <CommunityAssetImageField
                            communityId={communityId}
                            category={AssetCategory.PLAYLIST_BANNER}
                            value={REF_config.current.banner}
                            onChange={(url) => {
                                REF_config.current.banner = url;
                                bump();
                            }}
                            onUploadingChange={handleUploadingChange}
                            aspectRatio={4 / 3}
                            size={120}
                            imageMaxWidth={1600}
                            title="Redimensionar banner da playlist"
                            className="playlist-banner-input"
                        />
                        <Input.Text
                            className="mt-4"
                            autoFocus={true}
                            placeholder="Nome da playlist"
                            value={REF_config.current.name}
                            onInput={(v) => {
                                REF_config.current.name = v;
                            }}
                            label="Insira o nome da playlist"
                            size={50}
                        />
                        <Input.Text
                            className="mt-2"
                            multiline
                            placeholder="Descrição da playlist"
                            value={REF_config.current.description}
                            onInput={(v) => {
                                REF_config.current.description = v;
                            }}
                            label="Adicione uma descrição resumida para a playlist"
                            size={250}
                        />
                    </div>
                ) : null
            }
        />
    );
}
